import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import {
  User, X, Loader2, Building2, Mail, Phone, MapPin, Calendar,
  MessageSquare, Plus, Check, Trash2, FileText, Download,
} from 'lucide-react';
import { JURISDICTION_NAMES } from '../../lib/constants';
import {
  STATUS_LABELS, JURISDICTION_FLAGS, formatDate, getScoreColor,
} from './adminUtils';

export const UserDetailModal = ({
  user,
  onClose,
  loading,
  assessments,
  newNote,
  setNewNote,
  savingNote,
  updatingStatus,
  onUpdateStatus,
  onAddNote,
  onDeleteNote,
  onSwitchToAssessment,
  onDownloadPDF,
}) => {
  if (!user) return null;
  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" data-testid="user-detail-modal">
      <div className="bg-slate-800 rounded-xl border border-slate-700 w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-slate-800 border-b border-slate-700 p-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <User className="h-5 w-5 text-[#3B82F6]" />
            User Details
          </h2>
          <Button variant="ghost" size="sm" onClick={onClose} className="text-slate-400 hover:text-white" data-testid="close-user-detail">
            <X className="h-5 w-5" />
          </Button>
        </div>

        {loading ? (
          <div className="p-8 flex justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-[#3B82F6]" />
          </div>
        ) : (
          <div className="p-6 space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <InfoCell icon={Building2} label="Company" value={user.company_name} />
              <InfoCell icon={User} label="Contact Person" value={user.person_name} />
              <InfoCell icon={Mail} label="Email" linkHref={`mailto:${user.email}`} value={user.email} valueClass="text-[#3B82F6] hover:underline" />
              <InfoCell icon={Phone} label="Phone" linkHref={`tel:${user.phone}`} value={user.phone} valueClass="text-[#3B82F6] hover:underline" />
              <InfoCell icon={MapPin} label="Country" value={user.country} />
              <InfoCell icon={Calendar} label="Registered" value={formatDate(user.created_at)} />
            </div>

            {/* Follow-up Status */}
            <div className="bg-slate-700/30 rounded-lg p-4">
              <p className="text-slate-400 text-xs mb-3">Follow-up Status</p>
              <div className="flex gap-2 flex-wrap">
                {Object.entries(STATUS_LABELS).map(([status, label]) => (
                  <Button
                    key={status}
                    variant={user.followup_status === status ? 'default' : 'outline'}
                    size="sm"
                    data-testid={`followup-status-${status}`}
                    onClick={() => onUpdateStatus(user.id, status)}
                    disabled={updatingStatus}
                    className={user.followup_status === status
                      ? 'bg-[#3B82F6]'
                      : 'border-slate-600 text-slate-300 hover:bg-slate-700'}
                  >
                    {user.followup_status === status && <Check className="h-3 w-3 mr-1" />}
                    {label}
                  </Button>
                ))}
              </div>
            </div>

            {/* Follow-up Notes */}
            <div className="bg-slate-700/30 rounded-lg p-4">
              <p className="text-slate-400 text-xs mb-3 flex items-center gap-2">
                <MessageSquare className="h-4 w-4" />
                Follow-up Notes
              </p>
              <div className="flex gap-2 mb-4">
                <Input
                  data-testid="followup-note-input"
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  placeholder="Add a note about this user..."
                  className="bg-slate-700 border-slate-600 text-white flex-1"
                  onKeyDown={(e) => e.key === 'Enter' && onAddNote(user.id)}
                />
                <Button
                  data-testid="add-note-btn"
                  onClick={() => onAddNote(user.id)}
                  disabled={savingNote || !newNote.trim()}
                  className="bg-[#3B82F6] hover:bg-[#2563EB]"
                >
                  {savingNote ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                </Button>
              </div>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {(user.followup_notes || []).length === 0 ? (
                  <p className="text-slate-500 text-sm text-center py-2">No notes yet</p>
                ) : (
                  [...(user.followup_notes || [])].reverse().map((note, idx) => {
                    const originalIdx = (user.followup_notes?.length || 0) - 1 - idx;
                    return (
                      <div key={`${note.created_at}-${originalIdx}`} className="bg-slate-800 rounded p-3 flex items-start justify-between gap-2">
                        <div>
                          <p className="text-white text-sm">{note.note}</p>
                          <p className="text-slate-500 text-xs mt-1">{formatDate(note.created_at)}</p>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          data-testid={`delete-note-${originalIdx}`}
                          onClick={() => onDeleteNote(user.id, originalIdx)}
                          className="text-slate-500 hover:text-red-400 h-6 w-6 p-0"
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            {/* User's Assessments */}
            <div>
              <p className="text-slate-400 text-xs mb-3 flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Assessments ({assessments.length})
              </p>
              <div className="space-y-2">
                {assessments.length === 0 ? (
                  <p className="text-slate-500 text-sm text-center py-4 bg-slate-700/30 rounded-lg">No assessments yet</p>
                ) : (
                  assessments.map((assessment) => (
                    <div
                      key={assessment.id}
                      className="bg-slate-700/30 rounded-lg p-3 flex items-center justify-between cursor-pointer hover:bg-slate-700/50"
                      onClick={() => onSwitchToAssessment(assessment)}
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-xl">{JURISDICTION_FLAGS[assessment.jurisdiction]}</span>
                        <div>
                          <p className="text-white text-sm">{JURISDICTION_NAMES[assessment.jurisdiction]}</p>
                          <p className="text-slate-500 text-xs">{formatDate(assessment.completed_at || assessment.created_at)}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {assessment.status === 'completed' ? (
                          <>
                            <Badge className={getScoreColor(assessment.scores?.overall?.level)}>
                              {assessment.scores?.overall?.percentage}%
                            </Badge>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                onDownloadPDF(assessment.id, user.company_name, assessment.jurisdiction);
                              }}
                              className="border-slate-600 text-slate-300 hover:bg-slate-700 h-7 w-7 p-0"
                            >
                              <Download className="h-3 w-3" />
                            </Button>
                          </>
                        ) : (
                          <Badge className="bg-[#F59E0B]/10 text-[#F59E0B]">In Progress</Badge>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const InfoCell = ({ icon: Icon, label, value, linkHref, valueClass }) => (
  <div className="bg-slate-700/30 rounded-lg p-4">
    <p className="text-slate-400 text-xs mb-1">{label}</p>
    {linkHref ? (
      <a href={linkHref} className={`${valueClass || 'text-white'} flex items-center gap-2`}>
        <Icon className="h-4 w-4" />
        {value}
      </a>
    ) : (
      <p className={`${valueClass || 'text-white'} font-semibold flex items-center gap-2`}>
        <Icon className="h-4 w-4 text-[#3B82F6]" />
        {value}
      </p>
    )}
  </div>
);
